<template>
  <main class="instructor-form-page">
    <CompanyBreadcrumb
      :aria-label="pageTitle"
      :home-aria-label="$t('general.home')"
      :items="breadcrumbItems"
    />

    <div class="section-header instructor-form-header mb-lg-2 border-0 uk-flex-middle">
      <div class="section-header-left">
        <h2 class="instructor-form-title">
          <span>{{ pageTitle }}</span>
        </h2>
      </div>
      <div class="section-header-right">
        <button
          class="btn btn-light instructor-form-list-btn"
          type="button"
          @click="goToList"
        >
          <i class="uil-list-ul"></i> {{ $t("general.instructor_list") }}
        </button>
      </div>
    </div>

    <div v-if="formErrors.length" class="mb-3">
      <div
        v-for="(error, index) in formErrors"
        :key="`instructor-error-${index}`"
        class="uk-alert-danger"
        uk-alert
      >
        <p class="mb-0 text-danger">
          <i class="uil-exclamation-triangle"></i> {{ error }}
        </p>
      </div>
    </div>

    <div v-if="loading" class="card instructor-form-card">
      <default-loading></default-loading>
    </div>

    <form
      v-else
      class="card regular-form instructor-form instructor-form-card"
      @submit.prevent="saveInstructor"
    >
      <div class="uk-grid-collapse uk-child-width-1-2@m instructor-form-grid" uk-grid>
        <div class="instructor-form-column">
          <h4 class="instructor-form-section-title">
            <i class="uil-user-circle"></i>
            {{ $t("general.user_and_type_information") }}
          </h4>

          <div class="form-group">
            <label class="col-form-label">
              {{ $t("general.user") }} <span class="required">*</span>
            </label>
            <multiselect
              v-model="selectedUser"
              :options="userOptions"
              :loading="usersLoading"
              :disabled="isEditMode || usersLoading"
              :allow-empty="false"
              :close-on-select="true"
              :searchable="true"
              label="name"
              track-by="id"
              :placeholder="$t('general.select_user')"
              :select-label="$t('general.select')"
              :selected-label="$t('general.selected')"
              class="instructor-user-select"
              deselect-label=""
            >
              <template #option="{ option }">
                <div class="instructor-user-option">
                  <img
                    :src="option.photo"
                    :alt="option.name"
                    @error="useFallbackPhoto"
                  />
                  <span>
                    <strong>{{ option.name }}</strong>
                    <small>{{
                      option.email || $t("general.email_not_found")
                    }}</small>
                  </span>
                </div>
              </template>
              <template #singleLabel="{ option }">
                <span>{{ option.name }} — {{ option.email }}</span>
              </template>
              <template #noResult>
                <div class="no-results">
                  {{ $t("general.no_results_found") }}
                </div>
              </template>
              <template #noOptions>
                <span>{{ $t("general.no_options") }}</span>
              </template>
            </multiselect>
          </div>

          <div v-if="selectedUser" class="selected-user-summary">
            <img
              :src="selectedUser.photo"
              :alt="selectedUser.name"
              @error="useFallbackPhoto"
            />
            <div>
              <strong>{{ selectedUser.name }}</strong>
              <span>{{
                selectedUser.email || $t("general.email_not_found")
              }}</span>
              <span v-if="selectedUser.title || selectedUser.department">
                {{
                  selectedUser.title || $t("general.title_not_specified")
                }}
                <template v-if="selectedUser.department">
                  · {{ selectedUser.department }}
                </template>
              </span>
            </div>
          </div>

          <div class="form-group mt-4">
            <label class="col-form-label" for="instructor-type">
              {{ $t("general.instructor_type") }}
              <span class="required">*</span>
            </label>
            <select
              id="instructor-type"
              v-model="form.type"
              class="form-control"
              required
            >
              <option :value="instructorTypes.INTERNAL">
                {{ $t("general.internal_instructor") }}
              </option>
              <option :value="instructorTypes.EXTERNAL">
                {{ $t("general.external_instructor") }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label class="col-form-label" for="instructor-institution">
              {{ $t("general.institution_name") }}
            </label>
            <input
              id="instructor-institution"
              v-model.trim="form.institutionName"
              class="form-control"
              type="text"
              :placeholder="$t('general.institution_name_placeholder')"
            />
          </div>

          <div class="form-group">
            <label class="col-form-label" for="instructor-status">
              {{ $t("general.status") }} <span class="required">*</span>
            </label>
            <select
              id="instructor-status"
              v-model.number="form.statusId"
              class="form-control"
              required
            >
              <option :value="instructorStatuses.ACTIVE">
                {{ $t("general.active") }}
              </option>
              <option :value="instructorStatuses.PASSIVE">
                {{ $t("general.passive") }}
              </option>
            </select>
          </div>
        </div>

        <div class="instructor-form-column">
          <h4 class="instructor-form-section-title">
            <i class="uil-award"></i>
            {{ $t("general.expertise_information") }}
          </h4>

          <div class="form-group">
            <label class="col-form-label" for="instructor-bio">{{
              $t("general.about")
            }}</label>
            <textarea
              id="instructor-bio"
              v-model.trim="form.bio"
              class="form-control"
              rows="5"
              :placeholder="$t('general.instructor_bio_placeholder')"
            ></textarea>
          </div>

          <div class="form-group">
            <label class="col-form-label" for="instructor-expertise">
              {{ $t("general.expertise_areas") }}
            </label>
            <multiselect
              id="instructor-expertise"
              ref="expertiseTagSelect"
              v-model="form.expertiseAreas"
              :allow-empty="true"
              :close-on-select="false"
              :hide-selected="true"
              :multiple="true"
              :max-height="80"
              :open-direction="'bottom'"
              :options="expertiseOptions"
              :placeholder="form.expertiseAreas.length ? '' : $t('general.add')"
              :searchable="true"
              :show-no-options="false"
              :show-no-results="false"
              :tag-placeholder="$t('general.add')"
              :taggable="true"
              class="instructor-tag-select"
              deselect-label=""
              select-label=""
              selected-label=""
              @tag="addExpertiseArea"
            />
          </div>

          <div class="form-group">
            <label class="col-form-label" for="instructor-certifications">
              {{ $t("general.educations_and_certificates") }}
            </label>
            <multiselect
              id="instructor-certifications"
              ref="educationCertificateTagSelect"
              v-model="form.educationsCertificates"
              :allow-empty="true"
              :close-on-select="false"
              :hide-selected="true"
              :multiple="true"
              :max-height="80"
              :open-direction="'bottom'"
              :options="educationCertificateOptions"
              :placeholder="
                form.educationsCertificates.length ? '' : $t('general.add')
              "
              :searchable="true"
              :show-no-options="false"
              :show-no-results="false"
              :tag-placeholder="$t('general.add')"
              :taggable="true"
              class="instructor-tag-select"
              deselect-label=""
              select-label=""
              selected-label=""
              @tag="addEducationCertificate"
            />
          </div>

          <div class="form-group">
            <label class="col-form-label" for="instructor-experience">
              {{ $t("general.experience") }}
            </label>
            <div class="input-group instructor-form-experience">
              <input
                id="instructor-experience"
                v-model.number="form.experienceYears"
                class="form-control"
                type="number"
                min="0"
                max="100"
              />
              <div class="input-group-append">
                <span class="input-group-text">{{ $t("general.year") }}</span>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="col-form-label" for="instructor-linkedin">
              {{ $t("general.linkedin_profile") }}
            </label>
            <input
              id="instructor-linkedin"
              v-model.trim="form.linkedinUrl"
              class="form-control"
              type="url"
              placeholder="https://www.linkedin.com/in/..."
            />
          </div>
        </div>
      </div>

      <div class="instructor-form-note">
        <i class="uil-info-circle"></i>
        <span>
          {{ $t("general.instructor_user_information_note") }}
        </span>
      </div>

      <div class="uk-card-footer instructor-form-actions">
        <button
          class="btn btn-light instructor-form-cancel-btn"
          type="button"
          @click="goToList"
        >
          {{ $t("general.give_up") }}
        </button>
        <button
          class="btn btn-default instructor-form-save-btn"
          type="submit"
          :disabled="saving"
        >
          <i
            :class="saving ? 'uil-spinner-alt instructor-spin' : 'uil-check'"
          ></i>
          {{
            saving
              ? $t("general.saving")
              : isEditMode
              ? $t("general.save_changes")
              : $t("general.add_instructor")
          }}
        </button>
      </div>
    </form>
  </main>
</template>

<script>
import Multiselect from "vue-multiselect";
import instructorService, {
  INSTRUCTOR_STATUSES,
  INSTRUCTOR_TYPES,
} from "@/core/services/instructor.service";
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";
import CompanyBreadcrumb from "@/view/company/components/breadcrumb/CompanyBreadcrumb.vue";

const FALLBACK_PHOTO = "https://cdn.anzera.com.tr/softinya/not-found.png";

const emptyForm = () => ({
  type: INSTRUCTOR_TYPES.INTERNAL,
  institutionName: "",
  expertiseAreas: [],
  bio: "",
  experienceYears: null,
  educationsCertificates: [],
  linkedinUrl: "",
  statusId: INSTRUCTOR_STATUSES.ACTIVE,
});

export default {
  name: "InstructorAdd",
  components: { DefaultLoading, Multiselect, CompanyBreadcrumb },
  data() {
    return {
      form: emptyForm(),
      selectedUser: null,
      userOptions: [],
      expertiseOptions: [],
      educationCertificateOptions: [],
      loading: false,
      usersLoading: false,
      saving: false,
      formErrors: [],
      instructorTypes: INSTRUCTOR_TYPES,
      instructorStatuses: INSTRUCTOR_STATUSES,
    };
  },
  computed: {
    isEditMode() {
      return this.$route.name === "company.instructors.edit";
    },
    pageTitle() {
      return this.isEditMode
        ? this.$t("general.edit_instructor")
        : this.$t("general.add_instructor");
    },
    breadcrumbItems() {
      return [
        { name: this.$t("general.instructors") },
        { name: this.pageTitle },
      ];
    },
  },
  methods: {
    async initializePage() {
      this.loading = true;
      this.formErrors = [];

      try {
        if (this.isEditMode) {
          await this.loadInstructor();
        } else {
          await this.loadUsers();
        }
      } catch (error) {
        this.formErrors = [
          this.getErrorMessage(
            error,
            this.$t("general.page_information_load_failed")
          ),
        ];
      } finally {
        this.loading = false;
      }
    },
    async loadUsers() {
      this.usersLoading = true;
      try {
        this.userOptions = await instructorService.listUsers();
      } finally {
        this.usersLoading = false;
      }
    },
    async loadInstructor() {
      const instructor = await instructorService.get(this.$route.params.id);
      const expertiseAreas = [...(instructor.expertiseAreas || [])];
      const educationsCertificates = [
        ...(instructor.educationsCertificates || []),
      ];
      this.selectedUser = instructor.user;
      this.userOptions = [instructor.user];
      this.expertiseOptions = [...expertiseAreas];
      this.educationCertificateOptions = [...educationsCertificates];
      this.form = {
        type: instructor.type,
        institutionName: instructor.institutionName,
        expertiseAreas,
        bio: instructor.bio,
        experienceYears: instructor.experienceYears,
        educationsCertificates,
        linkedinUrl: instructor.linkedinUrl,
        statusId: instructor.statusId,
      };
    },
    addExpertiseArea(value) {
      this.addTag("expertiseAreas", "expertiseOptions", value);
      this.closeTagSelect("expertiseTagSelect");
    },
    addEducationCertificate(value) {
      this.addTag(
        "educationsCertificates",
        "educationCertificateOptions",
        value
      );
      this.closeTagSelect("educationCertificateTagSelect");
    },
    closeTagSelect(refName) {
      this.$nextTick(() => {
        this.$refs[refName]?.deactivate();
      });
    },
    addTag(formField, optionsField, value) {
      const tag = String(value || "").trim();
      if (!tag) return;

      const exists = this.form[formField].some(
        (item) => item.toLocaleLowerCase("tr-TR") === tag.toLocaleLowerCase("tr-TR")
      );
      if (exists) return;

      this[optionsField].push(tag);
      this.form[formField].push(tag);
    },
    validateForm() {
      const errors = [];
      if (!this.selectedUser) {
        errors.push(this.$t("general.user_selection_required"));
      }
      if (!Object.values(INSTRUCTOR_TYPES).includes(this.form.type)) {
        errors.push(this.$t("general.valid_instructor_type_required"));
      }
      if (![0, 1].includes(Number(this.form.statusId))) {
        errors.push(this.$t("general.valid_status_required"));
      }
      this.formErrors = errors;
      return errors.length === 0;
    },
    buildPayload() {
      const payload = {
        type: this.form.type,
        institution_name: this.form.institutionName || null,
        expertise_areas: [...this.form.expertiseAreas],
        bio: this.form.bio || null,
        experience_years:
          this.form.experienceYears === null || this.form.experienceYears === ""
            ? null
            : Number(this.form.experienceYears),
        educations_certificates: [...this.form.educationsCertificates],
        linkedin_url: this.form.linkedinUrl || null,
        status_id: Number(this.form.statusId),
      };

      if (!this.isEditMode) payload.user_id = this.selectedUser.id;
      return payload;
    },
    async saveInstructor() {
      if (!this.validateForm()) return;

      this.saving = true;
      this.formErrors = [];
      try {
        let savedInstructor;
        if (this.isEditMode) {
          savedInstructor = await instructorService.update(
            this.$route.params.id,
            this.buildPayload()
          );
        } else {
          savedInstructor = await instructorService.create(
            this.buildPayload()
          );
        }

        this.$router.push({
          name: "company.instructors.list",
          query: {
            saved: savedInstructor.id || this.$route.params.id,
            action: this.isEditMode ? "updated" : "created",
          },
        });
      } catch (error) {
        this.formErrors = [
          this.getErrorMessage(
            error,
            this.$t("general.instructor_save_failed")
          ),
        ];
        window.scrollTo({ top: 0, behavior: "smooth" });
      } finally {
        this.saving = false;
      }
    },
    getErrorMessage(error, fallback) {
      const responseData = error?.response?.data;
      if (responseData?.message) return responseData.message;
      if (responseData?.error) return responseData.error;
      if (responseData?.errors) {
        return Object.values(responseData.errors).flat().join(" ");
      }
      return error?.message || fallback;
    },
    useFallbackPhoto(event) {
      if (event.target.src !== FALLBACK_PHOTO)
        event.target.src = FALLBACK_PHOTO;
    },
    goToList() {
      this.$router.push({ name: "company.instructors.list" });
    },
  },
  mounted() {
    this.initializePage();
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.instructor-form-page {
  --instructor-primary: var(--primaryColor, #3e416d);
  --instructor-text: #1e293b;
  --instructor-muted: #64748b;
  --instructor-border: #e2e8f0;
  min-width: 0;
}

.instructor-form-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.instructor-form-header .section-header-right > * {
  margin-left: 0;
}

.instructor-form-title {
  margin: 0;
  color: var(--instructor-text);
  font-size: 19px;
  font-weight: 600;
  line-height: 1.3;
}

.instructor-form-card {
  overflow: visible;
  margin-top: 0;
  padding: 0;
  border: 1px solid var(--instructor-border);
  border-radius: 14px;
  box-shadow: none;
}

.instructor-form-grid,
.instructor-form-column {
  min-width: 0;
}

.instructor-form-column {
  padding: 32px;
}

.instructor-form-column + .instructor-form-column {
  border-left: 1px solid var(--instructor-border);
}

.instructor-form-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
  color: var(--instructor-text);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
}

.instructor-form .col-form-label {
  display: block;
  margin-bottom: 8px;
  padding: 0;
  color: var(--instructor-text);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

.instructor-form .form-control {
  box-sizing: border-box;
  width: 100%;
  height: calc(2.25rem + 2px);
  padding: 0.375rem 0.75rem;
  border: 1px solid #cfd5df;
  border-radius: 10px;
  color: var(--instructor-text);
  font-size: 15px;
  line-height: 1.5;
}

.instructor-form input.form-control,
.instructor-form textarea.form-control {
  -webkit-appearance: none;
  appearance: none;
}

.instructor-form .form-control:focus {
  border-color: var(--instructor-primary);
  box-shadow: 0 0 0 3px rgba(62, 65, 109, 0.16);
  outline: none;
}

.instructor-form textarea.form-control {
  height: auto;
  min-height: 120px;
}

.instructor-form .form-group {
  margin-bottom: 16px;
}

.instructor-form-experience {
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  width: 100%;
}

.instructor-form-experience .form-control {
  min-width: 0;
  width: auto;
  flex: 1 1 auto;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.instructor-form-experience .input-group-append,
.instructor-form-experience .input-group-text {
  flex: 0 0 auto;
  white-space: nowrap;
}

.instructor-form-experience .input-group-text {
  display: flex;
  align-items: center;
  height: calc(2.25rem + 2px);
  padding: 0 12px;
  border: 1px solid #cfd5df;
  border-left: 0;
  border-radius: 0 10px 10px 0;
  color: var(--instructor-text);
  font-size: 15px;
  line-height: 1.5;
  background: #f8fafc;
}

.instructor-form-page ::v-deep .multiselect,
.instructor-form-page ::v-deep .instructor-user-select,
.instructor-form-page ::v-deep .instructor-tag-select {
  min-height: 40px;
  height: auto !important;
  overflow: visible;
  box-shadow: none !important;
}

.instructor-form-page ::v-deep .multiselect__tags,
.instructor-form-page ::v-deep .multiselect--active .multiselect__tags {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  box-sizing: border-box;
  min-height: 38px;
  padding: 8px 40px 8px 8px;
  border: 1px solid #cfd5df;
  border-radius: 10px;
  box-shadow: none !important;
}

.instructor-form-page ::v-deep .multiselect--active .multiselect__tags {
  border-color: var(--instructor-primary);
}

.instructor-form-page ::v-deep .multiselect__select {
  height: 40px;
}

.instructor-form-page ::v-deep .multiselect__input,
.instructor-form-page ::v-deep .multiselect__single,
.instructor-form-page ::v-deep .multiselect__placeholder {
  margin-bottom: 0;
  padding: 0;
  background: transparent;
  border: 0;
  box-shadow: none !important;
  outline: none;
  font-size: 15px;
  line-height: 1.5;
  -webkit-appearance: none;
  appearance: none;
}

.instructor-form-page ::v-deep .multiselect__input:focus,
.instructor-form-page ::v-deep .multiselect__single:focus {
  background: transparent;
  border: 0;
  -webkit-box-shadow: none !important;
  box-shadow: none !important;
  outline: none;
}

.instructor-form-page ::v-deep .multiselect__tag {
  color: #fff;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  background-color: var(--instructor-primary);
}

.instructor-form-page ::v-deep .multiselect__tag-icon {
  background-color: transparent;
  line-height: 2.2;
}

.instructor-form-page ::v-deep .multiselect__tag-icon:after {
  color: #fff;
}

.instructor-user-option {
  display: flex;
  align-items: center;
  gap: 10px;
}

.instructor-user-option img {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #eef2f6;
  object-fit: cover;
}

.instructor-user-option span {
  display: flex;
  flex-direction: column;
}

.instructor-user-option small {
  color: var(--instructor-muted);
  font-size: 13px;
}

.selected-user-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border: 1px solid var(--instructor-border);
  border-radius: 10px;
  background: #f8fafc;
}

.selected-user-summary img {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #eef2f6;
  object-fit: cover;
  object-position: center top;
}

.selected-user-summary div {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.selected-user-summary strong {
  color: var(--instructor-text);
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
}

.selected-user-summary span {
  color: var(--instructor-muted);
  font-size: 14px;
  line-height: 1.4;
}

.instructor-form-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 32px 16px;
  padding: 12px 16px;
  border-radius: 10px;
  background: #edf3ff;
  color: #356ac3;
  font-size: 14px;
  line-height: 1.5;
}

.instructor-form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 32px 32px;
  border-top: 1px solid var(--instructor-border);
  background: transparent;
}

.instructor-form-list-btn,
.instructor-form-cancel-btn,
.instructor-form-save-btn {
  padding: 10px 17px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 17px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.instructor-form-list-btn:active,
.instructor-form-cancel-btn:active,
.instructor-form-save-btn:active:not(:disabled) {
  transform: scale(0.98);
  opacity: 0.85;
}

.instructor-form-save-btn:disabled {
  opacity: 0.5;
  pointer-events: none;
}

.instructor-form-save-btn:focus-visible,
.instructor-form-cancel-btn:focus-visible,
.instructor-form-list-btn:focus-visible {
  outline: 3px solid rgba(62, 65, 109, 0.28);
  outline-offset: 2px;
}

.instructor-form-page .uk-alert {
  margin: 0 0 16px;
}

.instructor-form-page .uk-alert p {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
}

.instructor-spin {
  display: inline-block;
  animation: instructor-spin 0.8s linear infinite;
}

@keyframes instructor-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 959px) {
  .instructor-form-page {
    box-sizing: border-box;
    max-width: 100%;
    margin: 0;
    padding: 0 0 24px;
  }

  .instructor-form-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .instructor-form-header .section-header-left,
  .instructor-form-header .section-header-right {
    width: 100%;
  }

  .instructor-form-title {
    font-size: 20px;
    font-weight: 600;
    line-height: 1.4;
  }

  .instructor-form-section-title {
    font-size: 20px;
  }

  .instructor-form-page .uk-alert p {
    font-size: 16px;
    line-height: 1.5;
  }

  .instructor-form-column {
    width: 100%;
    padding: 16px;
  }

  .instructor-form-column + .instructor-form-column {
    border-top: 1px solid var(--instructor-border);
    border-left: 0;
  }

  .instructor-form .form-control {
    min-height: 48px;
    height: 48px;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 1.4;
  }

  .instructor-form textarea.form-control {
    height: auto;
    min-height: 120px;
  }

  .instructor-form-experience .form-control {
    min-width: 0;
    width: auto;
    flex: 1 1 auto;
  }

  .instructor-form-experience .input-group-text {
    min-height: 48px;
    height: 48px;
    padding: 0 16px;
    font-size: 16px;
    line-height: 1.4;
  }

  .instructor-form-page ::v-deep .multiselect,
  .instructor-form-page ::v-deep .instructor-user-select,
  .instructor-form-page ::v-deep .instructor-tag-select {
    min-height: 48px;
  }

  .instructor-form-page ::v-deep .multiselect__tags,
  .instructor-form-page ::v-deep .multiselect--active .multiselect__tags {
    min-height: 48px;
  }

  .instructor-form-page ::v-deep .multiselect__select {
    height: 48px;
  }

  .instructor-form-page ::v-deep .multiselect__input,
  .instructor-form-page ::v-deep .multiselect__single,
  .instructor-form-page ::v-deep .multiselect__placeholder {
    font-size: 16px;
    line-height: 1.4;
  }

  .selected-user-summary {
    gap: 16px;
    padding: 16px;
    border-radius: 14px;
  }

  .selected-user-summary span {
    overflow-wrap: anywhere;
  }

  .instructor-form-note {
    align-items: flex-start;
    margin: 0 16px 16px;
  }

  .instructor-form-actions {
    flex-direction: column;
    align-items: stretch;
    padding: 16px;
  }

  .instructor-form-list-btn,
  .instructor-form-cancel-btn,
  .instructor-form-save-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 48px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
  }
}

@media (hover: hover) and (pointer: fine) {
  .instructor-form-list-btn:hover,
  .instructor-form-cancel-btn:hover,
  .instructor-form-save-btn:hover:not(:disabled) {
    opacity: 0.92;
    cursor: pointer;
  }

  .instructor-form-page ::v-deep .multiselect__tag-icon:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .instructor-form-list-btn:active,
  .instructor-form-cancel-btn:active,
  .instructor-form-save-btn:active:not(:disabled) {
    transform: none;
  }
}
</style>
